import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Download, ExternalLink, FolderPlus, FileText, Loader2 } from 'lucide-react';
import { type ChatAttachmentRef } from '@/lib/api';
import { isChatAttachmentMimeInlineSafe } from '../utils';

/**
 * Text/markdown files we render inline ourselves (rather than in the browser's
 * built-in text viewer via a cross-origin iframe) so we control the colors — the
 * UA viewer follows `prefers-color-scheme: dark` and paints light text, which is
 * invisible against our white panel. Capped: huge files fall back to the iframe.
 */
const INLINE_TEXT_MAX_BYTES = 1_000_000;

interface FileViewerModalProps {
  attachment: ChatAttachmentRef;
  /** Inline-disposition URL — renders in-page rather than forcing a download. */
  url: string;
  onClose: () => void;
  /** Save-to-device (fetches a fresh attachment-disposition URL upstream). */
  onDownload: () => void;
  /** Save into the user's Fias files. Omitted for legacy vault-backed refs. */
  onSaveToVault?: () => void;
  savingToVault?: boolean;
}

type PreviewKind = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'markdown';

/**
 * Which in-page renderer (if any) to use for an attachment.
 *
 * Gated on `isChatAttachmentMimeInlineSafe` first, so this can never offer a
 * preview the server will refuse to serve inline: the download route clamps
 * anything not preview-safe to `Content-Disposition: attachment`, and an
 * `<iframe>` pointed at such a URL silently downloads instead of rendering.
 * That covers `text/html` and `image/svg+xml` (both scriptable), `text/xml`,
 * and every row stored before the file-type allowlist existed. They fall
 * through to the no-preview panel — open-in-new-tab / download / save.
 */
function previewKind(mimeType: string): PreviewKind | null {
  const mime = (mimeType || '').toLowerCase();
  if (!isChatAttachmentMimeInlineSafe(mime)) return null;
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  if (mime === 'text/markdown' || mime === 'text/x-markdown') return 'markdown';
  if (mime.startsWith('text/')) return 'text';
  return null;
}

export function FileViewerModal({
  attachment,
  url,
  onClose,
  onDownload,
  onSaveToVault,
  savingToVault,
}: FileViewerModalProps) {
  const kind = previewKind(attachment.mimeType);
  const isTextLike = kind === 'text' || kind === 'markdown';
  // Render text/markdown inline ourselves when it's small enough to fetch.
  const attemptInline = isTextLike && attachment.sizeBytes <= INLINE_TEXT_MAX_BYTES;

  const [text, setText] = useState<string | null>(null);
  const [textFailed, setTextFailed] = useState(false);

  // Fetch the text content for inline rendering. The chat-attachments bucket
  // allows cross-origin GET from the app origin (same CORS that the direct PUT
  // upload relies on), so this fetch succeeds without a server round-trip. On
  // any failure we fall back to the browser's iframe viewer.
  useEffect(() => {
    if (!attemptInline) return;
    let cancelled = false;
    setText(null);
    setTextFailed(false);
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => {
        if (!cancelled) setText(t);
      })
      .catch(() => {
        if (!cancelled) setTextFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url, attemptInline]);

  const showInline = attemptInline && !textFailed;
  const showIframe = kind === 'pdf' || (isTextLike && !showInline);

  // Close on Escape
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle, true);
    return () => document.removeEventListener('keydown', handle, true);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center animate-in fade-in-0 duration-150"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80" />

      {/* Content — stop propagation so clicking media doesn't close modal */}
      <div
        className="relative z-10 flex flex-col items-center max-w-[92vw] max-h-[92vh] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-10 right-0 p-2 text-white/70 hover:text-white transition-colors"
          title="Close (Esc)"
        >
          <X className="h-5 w-5" />
        </button>

        {kind === 'image' && (
          <img
            src={url}
            alt={attachment.fileName}
            className="max-w-[92vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
            draggable={false}
          />
        )}

        {kind === 'video' && (
          <video
            src={url}
            controls
            autoPlay
            className="max-w-[92vw] max-h-[80vh] rounded-lg shadow-2xl"
          />
        )}

        {kind === 'audio' && (
          <div className="bg-white rounded-lg p-6 shadow-2xl min-w-[320px]">
            <audio src={url} controls autoPlay className="w-full" />
          </div>
        )}

        {showInline && (
          // We control the colors here (white panel, dark text) so the content
          // stays readable regardless of the viewer's color scheme.
          <div
            className="w-[85vw] h-[80vh] overflow-auto rounded-lg shadow-2xl bg-white text-gray-900 px-8 py-6"
            style={{ colorScheme: 'light' }}
          >
            {text === null ? (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : kind === 'markdown' ? (
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
              </div>
            ) : (
              <pre className="whitespace-pre-wrap break-words font-mono text-sm">{text}</pre>
            )}
          </div>
        )}

        {showIframe && (
          // Cross-origin (S3) src — isolated from this origin by same-origin policy.
          // `colorScheme: light` nudges the UA viewer to paint dark-on-light.
          <iframe
            src={url}
            title={attachment.fileName}
            className="w-[85vw] h-[80vh] rounded-lg shadow-2xl bg-white"
            style={{ colorScheme: 'light' }}
          />
        )}

        {!kind && (
          <div className="bg-white rounded-lg p-8 shadow-2xl flex flex-col items-center gap-3 min-w-[320px] max-w-[90vw]">
            <FileText className="h-12 w-12 text-foreground/30" />
            <p className="text-sm text-foreground/70 text-center">
              Preview isn&apos;t available for this file type.
            </p>
          </div>
        )}

        {/* Filename + actions */}
        <div className="mt-3 flex flex-col items-center gap-2">
          <p className="text-sm text-white/60 truncate max-w-[60vw]">{attachment.fileName}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open in new tab
            </button>
            <button
              type="button"
              onClick={onDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
            {onSaveToVault && (
              <button
                type="button"
                onClick={onSaveToVault}
                disabled={savingToVault}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                {savingToVault ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FolderPlus className="h-3.5 w-3.5" />
                )}
                Save to Fias files
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
